import React, { useState, useMemo } from "react";
import {
  X,
  Check,
  XCircle,
  Building2,
  UserCheck,
  FileText,
  DollarSign,
  Phone,
  MapPin,
  Briefcase,
  CheckCircle,
} from "lucide-react";
import apiService from "../services/api";
import "../styles/EmployeeReferenceModal.css";

const EmployeeReferenceModal = ({ employees, onClose, onApproved }) => {
  const [employeeList, setEmployeeList] = useState(employees || []);
  const [selectedEmployees, setSelectedEmployees] = useState(new Set());
  const [expandedEmployee, setExpandedEmployee] = useState(null);
  const [loading, setLoading] = useState(false);

  // Filter only employees with reference data
  const employeesWithReference = useMemo(() => {
    return employeeList.filter(
      (emp) => emp.reference_data && emp.copied_from_other_partnership,
    );
  }, [employeeList]);

  // Group by customer/site
  const groupedBySite = useMemo(() => {
    const groups = {};
    employeesWithReference.forEach((emp) => {
      const siteName = emp.customer_name || emp.site_name || "ללא אתר";
      if (!groups[siteName]) {
        groups[siteName] = [];
      }
      groups[siteName].push(emp);
    });
    return groups;
  }, [employeesWithReference]);

  if (employeesWithReference.length === 0) {
    return (
      <div className='modal-overlay' onClick={onClose}>
        <div
          className='modal-content reference-modal'
          onClick={(e) => e.stopPropagation()}
        >
          <div className='modal-header'>
            <h2>✅ אין עובדים לשחזור מחברות אחרות</h2>
            <button className='modal-close' onClick={onClose}>
              <X size={24} />
            </button>
          </div>
          <div className='reference-empty'>
            <CheckCircle size={64} color='#10b981' />
            <p>כל העובדים החדשים נוצרו ללא נתוני עוגן</p>
            <button className='btn-primary' onClick={onClose}>
              סגור
            </button>
          </div>
        </div>
      </div>
    );
  }

  const toggleEmployee = (employeeId) => {
    const newSelected = new Set(selectedEmployees);
    if (newSelected.has(employeeId)) {
      newSelected.delete(employeeId);
    } else {
      newSelected.add(employeeId);
    }
    setSelectedEmployees(newSelected);
  };

  const selectAllBySite = (siteName) => {
    const siteEmployeeIds = groupedBySite[siteName].map(
      (emp) => emp.employee_id || emp.id,
    );
    const newSelected = new Set(selectedEmployees);

    // Check if all are already selected
    const allSelected = siteEmployeeIds.every((id) => newSelected.has(id));

    if (allSelected) {
      // Deselect all
      siteEmployeeIds.forEach((id) => newSelected.delete(id));
    } else {
      // Select all
      siteEmployeeIds.forEach((id) => newSelected.add(id));
    }
    setSelectedEmployees(newSelected);
  };

  const handleApproveSelected = async () => {
    if (selectedEmployees.size === 0) {
      alert("אנא בחר לפחות עובד אחד");
      return;
    }

    setLoading(true);
    try {
      // Approve all selected employees
      const approvePromises = Array.from(selectedEmployees).map((employeeId) =>
        apiService.approveNewEmployee(employeeId),
      );

      await Promise.all(approvePromises);

      // Remove approved employees from list
      const remaining = employeeList.filter(
        (emp) => !selectedEmployees.has(emp.employee_id || emp.id),
      );
      setEmployeeList(remaining);
      setSelectedEmployees(new Set());

      if (onApproved) onApproved();
    } catch (error) {
      console.error("Error approving employees:", error);
      alert("שגיאה באישור עובדים");
    } finally {
      setLoading(false);
    }
  };

  const handleApproveAll = async () => {
    setLoading(true);
    try {
      const allIds = employeesWithReference.map(
        (emp) => emp.employee_id || emp.id,
      );

      const approvePromises = allIds.map((employeeId) =>
        apiService.approveNewEmployee(employeeId),
      );

      await Promise.all(approvePromises);

      setEmployeeList([]);
      if (onApproved) onApproved();
    } catch (error) {
      console.error("Error approving all employees:", error);
      alert("שגיאה באישור עובדים");
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (employeeId) => {
    setExpandedEmployee(expandedEmployee === employeeId ? null : employeeId);
  };

  return (
    <div className='modal-overlay' onClick={onClose}>
      <div
        className='modal-content reference-modal'
        onClick={(e) => e.stopPropagation()}
      >
        <div className='modal-header'>
          <div className='header-title'>
            <UserCheck size={28} className='header-icon' />
            <div>
              <h2>עובדים לשחזור מחברות אחרות</h2>
              <p className='subtitle'>
                {employeesWithReference.length} עובדים נמצאו בחברות אחרות עם
                נתוני עוגן
              </p>
            </div>
          </div>
          <button className='modal-close' onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <div className='reference-instructions'>
          <div className='instruction-item'>
            <Building2 size={16} />
            <span>בחר אתר לבחירה/ביטול בחירה של כל העובדים באתר</span>
          </div>
          <div className='instruction-item'>
            <Check size={16} />
            <span>לחץ על עובד לצפייה בנתונים שיועתקו</span>
          </div>
        </div>

        <div className='reference-list'>
          {Object.entries(groupedBySite).map(([siteName, siteEmployees]) => (
            <div key={siteName} className='site-group'>
              <div className='site-header'>
                <label className='site-checkbox-label'>
                  <input
                    type='checkbox'
                    checked={siteEmployees.every((emp) =>
                      selectedEmployees.has(emp.employee_id || emp.id),
                    )}
                    onChange={() => selectAllBySite(siteName)}
                    className='site-checkbox'
                  />
                  <Building2 size={18} />
                  <span className='site-name'>{siteName}</span>
                  <span className='site-count'>({siteEmployees.length})</span>
                </label>
              </div>

              <div className='site-employees'>
                {siteEmployees.map((employee) => {
                  const empId = employee.employee_id || employee.id;
                  const isExpanded = expandedEmployee === empId;
                  const isSelected = selectedEmployees.has(empId);
                  const refData = employee.reference_data;

                  return (
                    <div
                      key={empId}
                      className={`reference-item ${isExpanded ? "expanded" : ""} ${isSelected ? "selected" : ""}`}
                    >
                      <div
                        className='reference-item-header'
                        onClick={() => toggleExpand(empId)}
                      >
                        <input
                          type='checkbox'
                          checked={isSelected}
                          onChange={(e) => {
                            e.stopPropagation();
                            toggleEmployee(empId);
                          }}
                          className='employee-checkbox'
                        />

                        <div className='employee-info'>
                          <h4>{employee.employee_name}</h4>
                          <span className='search-method'>
                            נמצא לפי{" "}
                            {refData.search_method === "name"
                              ? "שם"
                              : "מספר עובד"}
                          </span>
                        </div>

                        <div className='source-badge'>
                          <Briefcase size={14} />
                          <span>מלקח מ{refData.source_partnership_name}</span>
                        </div>

                        <div className='expand-icon'>
                          {isExpanded ? (
                            <X size={20} />
                          ) : (
                            <FileText size={20} />
                          )}
                        </div>
                      </div>

                      {isExpanded && (
                        <div className='reference-details'>
                          <div className='details-grid'>
                            {refData.data.hour_rate && (
                              <div className='detail-item'>
                                <DollarSign size={16} />
                                <span className='detail-label'>
                                  תעריף שעתי:
                                </span>
                                <span className='detail-value'>
                                  ₪{refData.data.hour_rate}
                                </span>
                              </div>
                            )}
                            {refData.data.weekend_hour_rate && (
                              <div className='detail-item'>
                                <DollarSign size={16} />
                                <span className='detail-label'>
                                  תעריף סופ"ש:
                                </span>
                                <span className='detail-value'>
                                  ₪{refData.data.weekend_hour_rate}
                                </span>
                              </div>
                            )}
                            {refData.data.bank && (
                              <div className='detail-item'>
                                <Building2 size={16} />
                                <span className='detail-label'>בנק:</span>
                                <span className='detail-value'>
                                  {refData.data.bank}
                                </span>
                              </div>
                            )}
                            {refData.data.account && (
                              <div className='detail-item'>
                                <FileText size={16} />
                                <span className='detail-label'>חשבון:</span>
                                <span className='detail-value'>
                                  {refData.data.account}
                                </span>
                              </div>
                            )}
                            {refData.data.branch && (
                              <div className='detail-item'>
                                <MapPin size={16} />
                                <span className='detail-label'>סניף:</span>
                                <span className='detail-value'>
                                  {refData.data.branch}
                                </span>
                              </div>
                            )}
                            {refData.data.phone_number && (
                              <div className='detail-item'>
                                <Phone size={16} />
                                <span className='detail-label'>טלפון:</span>
                                <span className='detail-value'>
                                  {refData.data.phone_number}
                                </span>
                              </div>
                            )}
                          </div>

                          {refData.notes && refData.notes.length > 0 && (
                            <div className='notes-section'>
                              <h5>
                                <FileText size={14} />
                                הערות מהחברה המקורית ({refData.notes.length}):
                              </h5>
                              <div className='notes-list'>
                                {refData.notes.map((note, idx) => (
                                  <div key={idx} className='note-item'>
                                    <p className='note-text'>{note.note}</p>
                                    <span className='note-meta'>
                                      {note.created_by} •{" "}
                                      {new Date(
                                        note.created_at,
                                      ).toLocaleDateString("he-IL")}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <div className='reference-footer'>
          <div className='selection-info'>
            <span>
              נבחרו {selectedEmployees.size} מתוך{" "}
              {employeesWithReference.length} עובדים
            </span>
          </div>

          <div className='actions'>
            <button
              className='btn-secondary'
              onClick={onClose}
              disabled={loading}
            >
              <XCircle size={18} />
              בטל
            </button>

            <button
              className='btn-approve-all'
              onClick={handleApproveAll}
              disabled={loading}
            >
              <Check size={18} />
              אשר הכל
            </button>

            <button
              className='btn-approve-selected'
              onClick={handleApproveSelected}
              disabled={loading || selectedEmployees.size === 0}
            >
              <Check size={18} />
              אשר נבחרים ({selectedEmployees.size})
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmployeeReferenceModal;
